import { downloadXlsxFile } from 'dashboard/helper/downloadHelper';
import DataCollectionReportsAPI from 'dashboard/api/dataCollectionReports';
import types from '../mutation-types';

const defaultSummary = () => ({
  totals: {
    totalCustomers: 0,
    completed: 0,
    inProgress: 0,
    pending: 0,
  },
  trend: [],
  fieldCoverage: [],
});

const defaultMeta = () => ({
  total_count: 0,
  current_page: 1,
  per_page: 25,
  total_pages: 0,
  has_next_page: false,
  has_prev_page: false,
});

const state = {
  summary: defaultSummary(),
  customers: [],
  meta: defaultMeta(),
  fields: [],
  uiFlags: {
    isFetchingSummary: false,
    isFetchingCustomers: false,
    isFetchingFields: false,
    isExporting: false,
  },
};

const getters = {
  getDataCollectionSummary: $state => $state.summary,
  getDataCollectionCustomers: $state => $state.customers,
  getDataCollectionCustomersMeta: $state => $state.meta,
  getDataCollectionFields: $state => $state.fields,
  getDataCollectionReportUIFlags: $state => $state.uiFlags,
};

const formatSummary = payload => {
  if (!payload || typeof payload !== 'object') {
    return defaultSummary();
  }

  const totals = payload.totals || {};
  return {
    totals: {
      totalCustomers: totals.total_customers || 0,
      completed: totals.completed || 0,
      inProgress: totals.in_progress || 0,
      pending: totals.pending || 0,
    },
    trend: (payload.trend || []).map(item => ({
      timestamp: item.timestamp,
      collectedFields: item.collected_fields || 0,
      uniqueCustomers: item.unique_customers || 0,
    })),
    fieldCoverage: (payload.field_coverage || []).map(item => ({
      id: item.id,
      fieldName: item.field_name,
      displayName: item.display_name,
      collectedCustomers: item.collected_customers || 0,
      completionRate: item.completion_rate || 0,
    })),
  };
};

const formatCustomer = customer => ({
  id: customer.id,
  name: customer.name,
  email: customer.email,
  phoneNumber: customer.phone_number,
  completionPercentage: customer.completion_percentage,
  collectedFieldsCount: customer.collected_fields_count,
  missingFields: customer.missing_fields || [],
  status: customer.status,
  lastCollectedAt: customer.last_collected_at,
  fields: (customer.fields || []).map(field => ({
    fieldName: field.field_name,
    displayName: field.display_name,
    value: field.value,
    collectedAt: field.collected_at,
    confidenceScore: field.confidence_score,
  })),
});

const formatFields = fields =>
  fields.map(field => ({
    id: field.id,
    fieldName: field.field_name,
    displayName: field.display_name,
    description: field.description,
    isRequired: field.is_required,
    fieldType: field.field_type,
    orderIndex: field.order_index,
  }));

export const actions = {
  async fetchDataCollectionSummary({ commit }, params = {}) {
    commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
      isFetchingSummary: true,
    });
    try {
      const { data } = await DataCollectionReportsAPI.getSummary(params);
      commit(types.SET_DATA_COLLECTION_REPORT_SUMMARY, formatSummary(data));
    } catch (error) {
      console.error(error);
      commit(types.SET_DATA_COLLECTION_REPORT_SUMMARY, defaultSummary());
      throw error;
    } finally {
      commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
        isFetchingSummary: false,
      });
    }
  },

  async fetchDataCollectionCustomers({ commit }, params = {}) {
    commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
      isFetchingCustomers: true,
    });
    try {
      const { data } = await DataCollectionReportsAPI.getCustomers(params);
      const customers = (data.customers || []).map(formatCustomer);
      commit(types.SET_DATA_COLLECTION_REPORT_CUSTOMERS, customers);
      commit(
        types.SET_DATA_COLLECTION_REPORT_CUSTOMERS_META,
        data.meta || defaultMeta()
      );
    } catch (error) {
      console.error(error);
      commit(types.SET_DATA_COLLECTION_REPORT_CUSTOMERS, []);
      commit(types.SET_DATA_COLLECTION_REPORT_CUSTOMERS_META, defaultMeta());
      throw error;
    } finally {
      commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
        isFetchingCustomers: false,
      });
    }
  },

  async fetchDataCollectionFields({ commit, state }) {
    if (state.fields.length) {
      return;
    }
    commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
      isFetchingFields: true,
    });
    try {
      const { data } = await DataCollectionReportsAPI.getFields();
      commit(
        types.SET_DATA_COLLECTION_REPORT_FIELDS,
        formatFields(data.fields || [])
      );
    } catch (error) {
      console.error(error);
      commit(types.SET_DATA_COLLECTION_REPORT_FIELDS, []);
      throw error;
    } finally {
      commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
        isFetchingFields: false,
      });
    }
  },

  async downloadDataCollectionCustomers({ commit }, params = {}) {
    commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
      isExporting: true,
    });
    try {
      const response = await DataCollectionReportsAPI.downloadCustomers(params);
      const fileName =
        params.fileName || 'data-collection-customers-report.xlsx';
      downloadXlsxFile(fileName, response.data);
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit(types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS, {
        isExporting: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_DATA_COLLECTION_REPORT_SUMMARY]($state, summary) {
    $state.summary = summary;
  },
  [types.SET_DATA_COLLECTION_REPORT_CUSTOMERS]($state, customers) {
    $state.customers = customers;
  },
  [types.SET_DATA_COLLECTION_REPORT_CUSTOMERS_META]($state, meta) {
    $state.meta = meta;
  },
  [types.SET_DATA_COLLECTION_REPORT_FIELDS]($state, fields) {
    $state.fields = fields;
  },
  [types.SET_DATA_COLLECTION_REPORTS_UI_FLAGS]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
